import { Frequency } from '@vendasta/galaxy/frequency';
import { ItemFeeRow, ItemRow, PriceSummary } from './interface';
import { getFrequencyPrices, ItemFrequencyPrice, ItemWholesalePrice } from '../item-price';
import { AppPrices, FeeAmountType, FrequencyPrice } from '@vendasta/marketplace-apps';
import { toGalaxyFrequency } from '@vendasta/billing-ui';
import { InventoryItemWithPricing } from '../inventory-item';
import {
  FuturePaymentScheduledTime,
  FuturePaymentItemsMapWithCurrency,
  ItemRowMapByDateAndDuration,
  frequencyConversionValue,
} from './interface';

export function resetItemFees(item: ItemRow): ItemFeeRow[] {
  if (!item?.fees && item.fees.length < 1) {
    return [];
  }
  const resetFees: ItemFeeRow[] = [];
  item.fees.forEach((fee) => {
    resetFees.push({ ...fee, retailAmount: fee.retailDefaultAmount, retailAmountTotal: fee.retailDefaultAmount });
  });
  return resetFees;
}

export function resetItemVariablePrice(item: ItemRow): ItemWholesalePrice {
  if (!item.wholesalePricing) {
    return null;
  }
  const resetVariablePrice = item.wholesalePricing;
  resetVariablePrice.frequencyPrices = resetVariablePrice.frequencyPrices.map((price) => {
    return {
      ...price,
      price: price.minimumPrice,
      total: price.minimumPrice * item.quantity,
    };
  });
  return resetVariablePrice;
}

export function parseItemFees(appPrices: AppPrices, item: InventoryItemWithPricing): ItemFeeRow[] {
  const editionId = item.type === 'app' ? item.edition?.editionId : undefined;
  const retailFrequencyPrices = getFrequencyPrices(appPrices, editionId, 'retail');
  const wholesaleFrequencyPrices = getFrequencyPrices(appPrices, editionId, 'wholesale');

  const fees: ItemFeeRow[] = [];
  const setupFee = parseSetupFee(wholesaleFrequencyPrices, retailFrequencyPrices, item);
  if (setupFee) {
    fees.push(setupFee);
  }

  const managementFees = parseManagementFees(wholesaleFrequencyPrices, item);
  fees.push(...managementFees);

  return fees;
}

export function convertHTMLControlValueToNumber(value: string | number): number {
  if (typeof value === 'string') {
    return parseFloat(value.replace(/,/g, ''));
  }
  return value;
}

export function calculateTotalWholesalePrice(items: ItemRow[]): PriceSummary {
  const priceSummary: PriceSummary = { oneTime: 0, monthly: 0, yearly: 0, currency: '' };

  items.forEach((item) => {
    if (!priceSummary.currency && item.wholesalePricing.currency) {
      priceSummary.currency = item.wholesalePricing.currency;
    }

    item.wholesalePricing.frequencyPrices?.forEach((price) => {
      addPriceBasedOnFrequency(price, priceSummary);
    });
    addFeesBasedOnFrequency(item, 'wholesale', priceSummary);
  });

  return priceSummary;
}

export function calculateTotalRetailPrice(items: ItemRow[]): PriceSummary {
  const priceSummary: PriceSummary = { oneTime: 0, monthly: 0, yearly: 0, currency: '' };

  items.forEach((item) => {
    if (!priceSummary.currency && item.retailPricing.currency) {
      priceSummary.currency = item.retailPricing.currency;
    }

    addPriceBasedOnFrequency(item.retailPricing, priceSummary);
    addFeesBasedOnFrequency(item, 'retail', priceSummary);
  });

  return priceSummary;
}

function addPriceBasedOnFrequency(itemPrice: ItemFrequencyPrice, priceSummary: PriceSummary) {
  if (!itemPrice.price) return;
  // one time total needs to be updated regardless of the type of frequency
  priceSummary.oneTime += itemPrice.total;
  if (itemPrice.frequency === Frequency.MONTHLY) {
    priceSummary.monthly += itemPrice.total;
  }
  if (itemPrice.frequency === Frequency.YEARLY) {
    priceSummary.yearly += itemPrice.total;
  }
}

function addFeesBasedOnFrequency(item: ItemRow, priceType: 'wholesale' | 'retail', priceSummary: PriceSummary) {
  item.fees.forEach((fee) => {
    if (priceType === 'wholesale') {
      const wholesaleTotal = fee.wholesaleAmount * item.quantity;
      priceSummary.oneTime += wholesaleTotal;
      if (fee.type === 'management' && fee.frequency === Frequency.MONTHLY) {
        priceSummary.monthly += wholesaleTotal;
      }
      if (fee.type === 'management' && fee.frequency === Frequency.YEARLY) {
        priceSummary.yearly += wholesaleTotal;
      }
    }

    if (priceType === 'retail') {
      priceSummary.oneTime += fee.retailAmountTotal ?? 0;
    }
  });
}

export function updatePercentageManagementFee(item: ItemRow, oldWholeSalePrice: number): void {
  item.fees.forEach((fee) => {
    if (fee.type === 'management' && fee.amountType === 'percentage') {
      const managementFeePercentage = Math.round((fee.wholesaleAmount / oldWholeSalePrice) * 100);
      const frequencyPrice = item.wholesalePricing.frequencyPrices.find((p) => p.frequency === fee.frequency);
      const wholesalePrice = frequencyPrice?.price || 0;
      fee.wholesaleAmount = calculatePercentageFee(managementFeePercentage, wholesalePrice);
    }
  });
}

export function calculatePercentageFee(percentage: number, price: number) {
  return Math.round((percentage * price) / 100);
}

function parseSetupFee(
  wholesalePrices: FrequencyPrice[],
  retailPrices: FrequencyPrice[],
  item: InventoryItemWithPricing,
): ItemFeeRow | null {
  const wholesaleSetupFeeTotal = wholesalePrices.reduce((acc, price) => acc + (price.setupFee || 0), 0);
  const retailSetupFeeTotal = parseRetailSetupFee(retailPrices, item);
  const retailDefaultSetupFee = parseRetailSetupFee(retailPrices, item, true);

  if (!wholesaleSetupFeeTotal && !retailSetupFeeTotal && !retailDefaultSetupFee) return null;

  return {
    type: 'setup',
    label: 'Setup fee',
    amountType: 'fixed',
    wholesaleAmount: wholesaleSetupFeeTotal,
    retailAmount: retailSetupFeeTotal,
    retailAmountTotal: retailSetupFeeTotal * item.quantity,
    frequency: Frequency.ONE_TIME,
    retailDefaultAmount: retailDefaultSetupFee,
  };
}

function parseRetailSetupFee(
  retailPrices: FrequencyPrice[],
  item: InventoryItemWithPricing,
  useDefault?: boolean,
): number {
  const revenueComponents = item.pricing?.currentRevenue?.revenueComponents;

  // default to marketplaceplace-apps fees if no revenue components are present
  if (!revenueComponents || useDefault) return retailPrices.reduce((acc, price) => acc + (price.setupFee || 0), 0);

  let retailSetupFeeTotal = 0;
  revenueComponents.forEach((rc, i) => {
    // one time is the default enum value
    const isOneTime = !rc.period;

    // skip the first revenue component since it represents the base price
    if (i > 0 && isOneTime) {
      retailSetupFeeTotal += rc.value ?? 0;
    }
  });
  return retailSetupFeeTotal;
}

function parseManagementFees(wholesaleFrequencyPrices: FrequencyPrice[], item: InventoryItemWithPricing): ItemFeeRow[] {
  const managementFees: ItemFeeRow[] = [];

  wholesaleFrequencyPrices.forEach((price) => {
    // exclude management fees when discounted wholesale price is zero
    if (price.instancePrice && getDiscountedBasePrice(price) <= 0) {
      return;
    }

    price.fees?.forEach((fee) => {
      if (fee.amountType === FeeAmountType.FEE_AMOUNT_TYPE_FIXED) {
        managementFees.push({
          type: 'management',
          label: 'Management fee',
          amountType: 'fixed',
          wholesaleAmount: fee.amount,
          frequency: toGalaxyFrequency(price.frequency),
        });
      }

      if (fee.amountType === FeeAmountType.FEE_AMOUNT_TYPE_PERCENTAGE) {
        let wholesalePrice = price.pricingRules?.[0]?.price;
        // use custom price if it is present
        if (item.pricing?.cost?.customPrice) {
          wholesalePrice = item.pricing.cost.customPrice;
        }
        managementFees.push({
          type: 'management',
          label: `Management fee (${fee.amount}%)`,
          amountType: 'percentage',
          wholesaleAmount: (fee.amount * wholesalePrice) / 100,
          frequency: toGalaxyFrequency(price.frequency),
        });
      }
    });
  });

  return managementFees;
}

function getDiscountedBasePrice(price: FrequencyPrice): number {
  const instancePrice = price.instancePrice?.total ?? 0;
  const setupFee = price.setupFee ?? 0;

  return instancePrice - setupFee;
}

export function calculateItemRowWholesaleFuturePayments(
  itemRows: ItemRow[],
  contractStartDate: Date,
): FuturePaymentItemsMapWithCurrency {
  function addToFuturePaymentsMap(
    futurePaymentsMap: ItemRowMapByDateAndDuration,
    numericStartDate: number,
    componentDuration: number,
    frequency: Frequency,
    totalPrice: number, // price * quantity
  ) {
    if (!futurePaymentsMap) {
      futurePaymentsMap = {};
    }
    if (!futurePaymentsGrouped[numericStartDate]) {
      futurePaymentsGrouped[numericStartDate] = {};
    }
    //Initialize duration dictionary if it doesnt exist
    if (!futurePaymentsMap[numericStartDate][componentDuration]) {
      futurePaymentsMap[numericStartDate][componentDuration] = {};
    }

    //initialize price if it doesnt exist, add to it otherwise
    if (!futurePaymentsMap[numericStartDate][componentDuration][frequency]) {
      futurePaymentsMap[numericStartDate][componentDuration][frequency] = totalPrice;
    } else {
      futurePaymentsMap[numericStartDate][componentDuration][frequency] += totalPrice;
    }
  }

  function calculateItemRowDuration(duration: number, startDate: Date, endDate: Date, frequency: Frequency): number {
    let itemRowDuration = duration;
    if (!itemRowDuration && endDate) {
      itemRowDuration = convertEndDateToDuration(startDate || new Date(), endDate, frequency);
    }
    if (frequency === Frequency.ONE_TIME) {
      itemRowDuration = FuturePaymentScheduledTime.NoRenewal;
    } else if (!itemRowDuration) {
      itemRowDuration = FuturePaymentScheduledTime.RenewIndefinitely;
    }

    return itemRowDuration;
  }

  if (!itemRows?.length || !contractStartDate) return {} as FuturePaymentItemsMapWithCurrency;

  const dueContractStartItems = getItemRowsDueToDate(itemRows, contractStartDate);

  const currencyCode: string = itemRows[0]?.wholesalePricing?.currency;

  const futurePaymentsGrouped: ItemRowMapByDateAndDuration = {};

  itemRows.forEach((itemRow) => {
    const billingPeriod = itemRow?.pricing?.billingPeriod;

    const startDate = billingPeriod?.startDate ? new Date(billingPeriod?.startDate) : undefined;
    const endDate = billingPeriod?.endDate ? new Date(billingPeriod?.endDate) : undefined;

    const duration = billingPeriod?.duration?.value;

    const frequencyPrices = itemRow?.wholesalePricing?.frequencyPrices || [];
    const fees = itemRow?.fees || [];
    let numericStartDate = startDate?.getTime();

    //If items is due now, set to StartChargingImmediately
    if (!numericStartDate || dueContractStartItems.includes(itemRow)) {
      numericStartDate = FuturePaymentScheduledTime.StartChargingImmediately;
    }

    frequencyPrices.forEach((frequencyPrice) => {
      const frequency = frequencyPrice.frequency || Frequency.ONE_TIME; // one time products and setup fees can return undefined period
      const price = frequencyPrice.total;
      const itemRowDuration = calculateItemRowDuration(duration, startDate, endDate, frequency);
      //Skip items that are not recurring or are free
      if (!isLineItemRecurring(startDate, contractStartDate, itemRowDuration, frequency) || !price) {
        return;
      }
      addToFuturePaymentsMap(futurePaymentsGrouped, numericStartDate, itemRowDuration, frequency, price);
    });

    // Add management and setup fees to future payments
    fees.forEach((fee) => {
      const frequency = fee.frequency || Frequency.ONE_TIME;
      const price = fee.wholesaleAmount * itemRow.quantity;
      const itemRowDuration = calculateItemRowDuration(duration, startDate, endDate, frequency);

      if (!isLineItemRecurring(startDate, contractStartDate, itemRowDuration, frequency) || !price) {
        return;
      }
      addToFuturePaymentsMap(futurePaymentsGrouped, numericStartDate, itemRowDuration, frequency, price);
    });
  });

  return { lineItemsMap: futurePaymentsGrouped, currencyCode: currencyCode };
}

// getLineItemsDueNow gets all line items that will be charged immediately
export function getItemRowsDueToDate(itemRows: ItemRow[], dueDate: Date): ItemRow[] {
  if (!itemRows?.length || !dueDate) return [];

  // Truncate to the day because we want to compare the start day in UTC
  const dueDateUtcDay = Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate());

  return itemRows.filter((item) => {
    const start = item.pricing?.billingPeriod?.startDate;
    if (!start) {
      return true; // If start is invalid or not defined, include the item
    }
    const startDate = new Date(start);
    // Truncate to the day because we want to compare with the current day in UTC
    const startUtcDay = Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate());

    return startUtcDay <= dueDateUtcDay;
  });
}

/*
filters out line items that are not recurring.
Recurring items are:
  - items with start date in the future
  - non-one-time items with duration > 1 or renewing indefinitely
*/
export function isLineItemRecurring(startDate: Date, dueDate: Date, duration: number, frequency: Frequency) {
  const startDateUTC = startDate
    ? Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate())
    : undefined;
  const dueDateUTC = dueDate
    ? Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate())
    : undefined;
  return (
    (startDateUTC && dueDateUTC && startDateUTC > dueDateUTC) ||
    ((duration > 1 || duration == FuturePaymentScheduledTime.RenewIndefinitely) && frequency !== Frequency.ONE_TIME)
  );
}

export function convertEndDateToDuration(startDate: Date, endDate: Date, frequency: Frequency) {
  if (frequency === Frequency.ONE_TIME || !frequencyConversionValue[frequency]) {
    return FuturePaymentScheduledTime.NoRenewal;
  }

  const yearDifference = endDate.getFullYear() - startDate.getFullYear();
  const monthDifference = endDate.getMonth() - startDate.getMonth();
  return Math.round((yearDifference * 12 + monthDifference) / frequencyConversionValue[frequency]);
}

/*
  getTotalWholesaleCostDueToDate returns wholesale cost, with management and setup fees for all items
  pricing billing period start date is up to the due date.
 */
export function getTotalWholesaleCostDueToDate(itemRows: ItemRow[], dueDate: Date): number {
  if (!itemRows?.length || !dueDate) return 0;
  const itemsDueNow = getItemRowsDueToDate(itemRows, dueDate);
  let total = 0;
  itemsDueNow.forEach((item) => {
    const frequencyPrices = item?.wholesalePricing?.frequencyPrices || [];
    const fees = item?.fees || [];
    const quantity = item?.quantity || 1;

    frequencyPrices.forEach((frequencyPrice) => {
      const price = frequencyPrice.total || 0;
      total += price;
    });

    fees.forEach((fee) => {
      const price = fee.wholesaleAmount || 0;
      total += price * quantity;
    });
  });

  return total;
}
